/**
 * Client-side console suppression.
 *
 * Replaces every console method with a wrapper that:
 *  1. Captures the call into a capped ring-buffer (for DevTools).
 *  2. In production, swallows the output so nothing leaks to the browser.
 *  3. In development, forwards to the real console as usual.
 *
 * Server-side this module is a no-op — New Relic auto-forwarding handles
 * server logs via `application_logging.forwarding` in newrelic.js.
 */

import { LogLevel, LogEntry, LogListener } from './types'
import { isDevelopment } from '../utils/environment'

const BUFFER_MAX = 500
type InterceptedConsoleMethod =
  | 'log'
  | 'info'
  | 'warn'
  | 'error'
  | 'debug'
  | 'trace'
  | 'assert'
  | 'table'
  | 'dir'
  | 'dirxml'

const INTERCEPTED_METHODS: Array<{
  method: InterceptedConsoleMethod
  level: LogLevel
}> = [
  { method: 'log', level: 'log' },
  { method: 'info', level: 'info' },
  { method: 'warn', level: 'warn' },
  { method: 'error', level: 'error' },
  { method: 'debug', level: 'debug' },
  { method: 'trace', level: 'debug' },
  { method: 'assert', level: 'error' },
  { method: 'table', level: 'info' },
  { method: 'dir', level: 'debug' },
  { method: 'dirxml', level: 'debug' }
]

const buffer: LogEntry[] = []
const listeners = new Set<LogListener>()

let originalConsole:
  | Partial<Record<InterceptedConsoleMethod, (...args: unknown[]) => void>>
  | null = null

function capture(level: LogLevel, args: unknown[]): void {
  const entry: LogEntry = { level, args, timestamp: Date.now() }

  buffer.push(entry)
  if (buffer.length > BUFFER_MAX) {
    buffer.shift()
  }

  listeners.forEach((fn) => {
    try {
      fn(entry)
    } catch {
      /* listener errors must never break the app */
    }
  })
}

function init(): void {
  if (typeof window === 'undefined') return
  if (originalConsole) return

  originalConsole = {}

  for (const { method, level } of INTERCEPTED_METHODS) {
    const originalMethod = console[method]
    if (typeof originalMethod !== 'function') {
      continue
    }

    const original = originalMethod.bind(console)
    originalConsole[method] = original

    console[method] = (...args: unknown[]) => {
      if (method === 'assert') {
        const [condition, ...assertArgs] = args
        if (condition) {
          if (isDevelopment) {
            original(...args)
          }
          return
        }
        capture(level, assertArgs.length ? assertArgs : ['Assertion failed'])
        if (isDevelopment) {
          original(...args)
        }
        return
      }

      if (method === 'trace') {
        capture(level, [
          ...args,
          { trace: new Error('Console trace').stack || 'Trace unavailable' }
        ])
        if (isDevelopment) {
          original(...args)
        }
        return
      }

      capture(level, args)

      if (isDevelopment) {
        original(...args)
      }
    }
  }

  window.addEventListener('error', (event) => {
    capture('error', [
      event.message || 'Unhandled error',
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      }
    ])
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    capture('error', [
      'Unhandled promise rejection',
      reason instanceof Error
        ? { name: reason.name, message: reason.message, stack: reason.stack }
        : reason
    ])
  })
}

init()

// ---------------------------------------------------------------------------
// Public API — used by useLogCapture and potentially by DevTools directly
// ---------------------------------------------------------------------------

export function subscribe(listener: LogListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getLogs(): LogEntry[] {
  return [...buffer]
}

export function clearLogs(): void {
  buffer.length = 0
}
